import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/context";

// Minimal client-side fallback so UI renders before first fetch. The server's
// DEFAULT_SITE_CONTENT is the authoritative source once loaded.
const FALLBACK = {
  hero_eyebrow: "Smartphone-Reparatur · Made in Germany",
  hero_title: "Handy kaputt? Wir reparieren.",
  hero_subtitle: "Schnell. Transparent. Professionell.",
  hero_description: "",
  section_popular_repairs: true,
  section_how_it_works: true,
  section_popular_brands: true,
  section_why_us: true,
  section_reviews: true,
  section_faq: true,
  section_location_preview: true,
};

const subscribers = new Set();
const sharedByLanguage = {};
const promisesByLanguage = {};

function fetchAndBroadcast(lang) {
  if (promisesByLanguage[lang]) return promisesByLanguage[lang];
  promisesByLanguage[lang] = api.get(`/site-content?lang=${lang}`)
    .then((r) => {
      sharedByLanguage[lang] = { ...FALLBACK, ...(r.data || {}) };
      subscribers.forEach((cb) => cb(lang, sharedByLanguage[lang]));
      return sharedByLanguage[lang];
    })
    .catch(() => sharedByLanguage[lang] || FALLBACK)
    .finally(() => { promisesByLanguage[lang] = null; });
  return promisesByLanguage[lang];
}

export function useSiteContent() {
  const { lang } = useI18n();
  const [content, setContent] = useState(sharedByLanguage[lang] || FALLBACK);
  const { pathname } = useLocation();

  useEffect(() => {
    setContent(sharedByLanguage[lang] || FALLBACK);
    let mounted = true;
    const cb = (updatedLang, c) => { if (mounted && updatedLang === lang) setContent(c); };
    subscribers.add(cb);
    fetchAndBroadcast(lang);
    return () => { mounted = false; subscribers.delete(cb); };
  }, [pathname, lang]);

  useEffect(() => {
    const refresh = () => fetchAndBroadcast(lang);
    const onStorage = (e) => { if (e.key === "handymeister:data-updated") fetchAndBroadcast(lang); };
    window.addEventListener("focus", refresh);
    window.addEventListener("handymeister:data-updated", refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("handymeister:data-updated", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, [lang]);

  return content;
}

/** Convenience getter that returns `content[key]` if defined,
 *  otherwise the provided fallback. Safe for missing keys. */
export function pick(content, key, fallback = "") {
  const v = content?.[key];
  if (v === undefined || v === null || v === "") return fallback;
  return v;
}

export function toTelHref(number) {
  return `tel:${(number || "").replace(/\s+/g, "")}`;
}
export function toWhatsAppHref(number) {
  return `https://wa.me/${(number || "").replace(/[^0-9]/g, "")}`;
}
